import { QbTagCategory } from '@exam-room/graphql';
import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  BookmarkBorderOutlinedIcon,
  BookmarkIcon,
  Form,
  Question,
  QuestionLayout,
} from '../../../components';
import { Choices, ExamReview, ExamReviewFooter, ExamReviewHeader } from '../../../containers';
import { useExamEvent, useNewExam } from '../../../providers';
import { PATHS } from '../../../routes/constants';

function ExamRoom() {
  const { t } = useTranslation();
  const { examCardId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({});
  const q = searchParams.get('q') ?? '';
  const page = searchParams.get('page') ?? '';
  const { examEvent } = useExamEvent();
  const { examPaper, questions, questionMap } = useNewExam();

  const currentQuestion = questionMap[q] ?? {};

  const { question, order, metadata, tags } = currentQuestion;

  let article: React.ReactNode;

  const enabledHighlight = false; // Keep it because the highlight feature is still here

  const tag = tags?.find((tag) => tag?.category === QbTagCategory.Part);

  const examRoomActions: JSX.Element[] = [
    <Form.CustomCheckbox name={`${q}.isBookmark`} id={`${q}.isBookmark`} key={`Bookmark.${q}`}>
      {({ open }) => (
        <div
          className={cn(
            'border-text-disabled text-body2 text-text-primary flex items-center gap-1.5 rounded-sm border px-3 py-1.5 leading-5',
            open && 'border-warning bg-warning-100',
          )}
        >
          {open ? (
            <BookmarkIcon className='text-warning-500 text-xl' />
          ) : (
            <BookmarkBorderOutlinedIcon className='text-xl' />
          )}
          <p className={cn('text-text-primary', open && 'text-warning-700')}>
            {t('examRoom.action.bookmark')}
          </p>
        </div>
      )}
    </Form.CustomCheckbox>,
  ];

  // if (type === QuestionType.CHOICES) {
  //   examRoomActions.push(
  //     <Tooltip
  //       key={`Elimination.${q}`}
  //       position='tr'
  //       content={<p className='w-44'>{t('examRoom.action.tooltip')}</p>}
  //     >
  //       <EliminationToggleButton open={showElimination} toggle={toggleElimination} />
  //     </Tooltip>,
  //   );
  // }

  useEffect(() => {
    const validQuestionId = q && (questionMap[q] || page == 'review');

    if (questions && !validQuestionId) {
      const params = new URLSearchParams();
      params.set('q', questions[0]?.id);
      params.set('page', 'exam');

      const query = params.toString();
      setSearchParams(query);
    }
  }, [questions]);

  const isReviewPage = page === 'review';

  if (!Object.keys(currentQuestion).length && !isReviewPage) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex h-full w-full flex-1 overflow-auto rounded-2xl bg-white shadow-sm',
        !enabledHighlight ? 'select-none' : 'select-text',
        isReviewPage && 'mx-auto max-w-3xl',
      )}
    >
      {isReviewPage ? (
        <div className='flex h-full w-full flex-col gap-5 bg-white p-10'>
          <ExamReviewHeader />
          <ExamReview />
          <ExamReviewFooter />
        </div>
      ) : (
        <QuestionLayout
          id='exam-highlight-section'
          article={article}
          className={cn(!article && 'min-w-[576px]')}
        >
          <div className='flex h-full w-full'>
            <Question
              key={`Question.${q}`}
              number={order}
              title={question}
              name={`${q}.answer`}
              choices={<Choices name={`${q}.answer`} choices={metadata?.choices} />}
              actions={examRoomActions}
              className={enabledHighlight ? 'select-text' : ''}
              tag={tag?.name ?? ''}
              paragraphs={metadata?.paragraphs}
            />
          </div>
        </QuestionLayout>
      )}
    </div>
  );
}

const ExamRoomRoute = {
  path: PATHS.Exam.Root,
  element: <ExamRoom />,
};

export default ExamRoomRoute;
